import { defaultTheme } from './defaultTheme.js';

/** Quasar built-in brand keys that also map to setCssVar on the host. */
export const QUASAR_BUILTIN_KEYS = Object.freeze([
    'anchor',
    'primary',
    'secondary',
    'accent',
    'positive',
    'negative',
    'info',
    'warning',
    'dark',
]);

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isColorValue(value) {
    if (typeof value !== 'string')
        return false;
    const trimmed = value.trim();
    return trimmed.startsWith('#') || trimmed.startsWith('rgb') || trimmed.startsWith('hsl');
}

/**
 * @param {unknown} colorVariables
 * @returns {Record<string, string>}
 */
export function normalizeColorVariables(colorVariables) {
    if (!colorVariables)
        return {};

    if (typeof colorVariables === 'string')
        return JSON.parse(colorVariables);

    if (Array.isArray(colorVariables)) {
        return colorVariables.reduce((acc, item) => {
            if (item?.varName && isColorValue(item.color))
                acc[item.varName] = item.color.trim();
            return acc;
        }, {});
    }

    if (typeof colorVariables === 'object')
        return { ...colorVariables };

    return {};
}

/**
 * @param {Record<string, string>} base
 * @param {Record<string, string>} [overrides]
 * @returns {Record<string, string>}
 */
export function mergeTheme(base, overrides) {
    const merged = { ...base };
    Object.keys(overrides || {}).forEach((key) => {
        const value = overrides[key];
        if (isColorValue(value))
            merged[key] = value.trim();
    });
    return merged;
}

/**
 * Apply color map as CSS custom properties on a document root.
 * Writes `--cc-*`, `--q-*`, and legacy `--v-*-base` for each key.
 *
 * @param {Record<string, string> | null | undefined} theme
 * @param {HTMLElement | Document | null} [root]
 * @param {{ setCssVar?: (name: string, value: string) => void }} [options]
 *   Optional Quasar `setCssVar` for host / Quasar guests.
 */
export function applyTheme(theme, root, options = {}) {
    if (!theme || typeof theme !== 'object')
        return;

    const el = resolveRoot(root);
    if (!el)
        return;

    const setCssVar = typeof options.setCssVar === 'function' ? options.setCssVar : null;

    Object.keys(theme).forEach((key) => {
        const value = theme[key];
        if (!isColorValue(value))
            return;
        const color = value.trim();
        el.style.setProperty(`--cc-${key}`, color);
        el.style.setProperty(`--q-${key}`, color);
        el.style.setProperty(`--v-${key}-base`, color);
        if (setCssVar && QUASAR_BUILTIN_KEYS.includes(key))
            setCssVar(key, color);
    });

    syncBuiltinAliases(theme, el, setCssVar);
}

/**
 * @param {string | null | undefined} color
 * @param {Record<string, string>} [activeTheme]
 * @returns {string | null | undefined}
 */
export function resolveThemeColor(color, activeTheme = defaultTheme) {
    if (color == null || color === '')
        return color;

    const trimmed = String(color).trim();
    if (
        trimmed.startsWith('#')
        || trimmed.startsWith('rgb')
        || trimmed.startsWith('hsl')
        || trimmed.startsWith('var(')
    ) {
        return trimmed;
    }

    if (Object.prototype.hasOwnProperty.call(activeTheme, trimmed))
        return `var(--v-${trimmed}-base)`;

    return trimmed;
}

/**
 * Apply the theme snapshot from a platform bridge and subscribe to updates.
 * @param {{ theme?: { get: () => { colors?: Record<string, string>, name?: string } | null, subscribe: (cb: Function) => () => void } }} platform
 * @param {HTMLElement | Document | null} [root]
 * @param {{ setCssVar?: (name: string, value: string) => void }} [options]
 * @returns {() => void} unsubscribe
 */
export function applyBridgeTheme(platform, root, options = {}) {
    const applySnapshot = (snapshot) => {
        const colors = snapshot?.colors;
        if (colors)
            applyTheme(colors, root, options);
    };

    applySnapshot(platform?.theme?.get?.() ?? null);

    if (typeof platform?.theme?.subscribe !== 'function')
        return () => {};

    return platform.theme.subscribe(applySnapshot);
}

/**
 * @param {HTMLElement | Document | null | undefined} root
 * @returns {HTMLElement | null}
 */
function resolveRoot(root) {
    if (typeof document === 'undefined')
        return null;
    if (!root)
        return document.documentElement;
    if (root.nodeType === 9)
        return root.documentElement;
    return root;
}

/**
 * @param {Record<string, string>} theme
 * @param {HTMLElement} el
 * @param {((name: string, value: string) => void) | null} setCssVar
 */
function syncBuiltinAliases(theme, el, setCssVar) {
    const negativeValue = theme.negative ?? theme.error ?? theme.danger ?? theme.destructive;
    if (negativeValue && isColorValue(negativeValue)) {
        const color = negativeValue.trim();
        el.style.setProperty('--cc-negative', color);
        el.style.setProperty('--q-negative', color);
        el.style.setProperty('--v-negative-base', color);
        if (setCssVar)
            setCssVar('negative', color);
    }

    const positiveValue = theme.positive ?? theme.success;
    if (positiveValue && isColorValue(positiveValue)) {
        const color = positiveValue.trim();
        el.style.setProperty('--cc-positive', color);
        el.style.setProperty('--q-positive', color);
        el.style.setProperty('--v-positive-base', color);
        if (setCssVar)
            setCssVar('positive', color);
    }
}
